import styled from 'styled-components';
import { formatTransactionTime, getTransactionAssetId, getTransactionFromAddresses, getTransactionToAddresses, serializeTransactionHash } from '@/utils/transaction';
import type { ColumnsType } from 'antd/es/table';
import { truncateAddress } from '@/utils/address';
import { CopyIcon } from '@/components/DataTable';
import AddressItem from '@/components/AddressItem';
import { ITransaction } from '@/api/commitment';
import { getTransactionAmount } from '@/utils/transaction';
import { CommitmentBaseDto, TransactionType, TransactionTypeName } from '@/types/commitment';
import AssetAmount from '@/components/AssetView/AssetAmount';
import { SupportedChainId } from '@packet/constants/AppSupportChains';
import AssetSymbol from '@/components/AssetView/AssetSymbol';
import { EyeOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';

// 获取交易表格列定义
export const getTransactionColumns = (chainId: SupportedChainId, onViewTransaction: (record: ITransaction) => void): ColumnsType<ITransaction> => [
  {
    title: 'Transactions Hash',
    dataIndex: 'txhash',
    key: 'txhash',
    width: 200,
    render: (_: string, record: ITransaction) => {
      const tx = serializeTransactionHash(record.txhash, record.tx_index);
      return (
        <HashContainer>
          <HashText>{truncateAddress(tx, 8, 6)}</HashText>
          <CopyIcon text={tx} />
        </HashContainer>
      );
    },
  },
  {
    title: 'Type',
    dataIndex: 'tx_type',
    key: 'tx_type',
    width: 120,
    render: (tx_type: TransactionType) => <TypeText>{TransactionTypeName[tx_type]}</TypeText>,
  },
  {
    title: 'From',
    dataIndex: 'inputs',
    key: 'inputs',
    width: 190,
    render: (inputs: CommitmentBaseDto[], record: ITransaction) => {
      const addresses = getTransactionFromAddresses(record);
      if (addresses.length > 0) {
        return (
          <div>
            {addresses.map((item) => (
              <AddressItem chainId={chainId} key={item.address} address={item.address} type={item.type} truncate={true} truncateConfig={{ start: 6, end: 4 }} showCopy={true} hoverShowCopy={true} />
            ))}
          </div>
        );
      }

      return <>Empty</>;
    },
  },
  {
    title: 'To',
    dataIndex: 'outputs',
    key: 'outputs',
    width: 190,
    render: (_: CommitmentBaseDto[], record: ITransaction) => {
      const addresses = getTransactionToAddresses(record);
      if (addresses.length > 0) {
        return (
          <div>
            {addresses.map((item) => (
              <AddressItem chainId={chainId} key={item.address} address={item.address} type={item.type} truncate={true} truncateConfig={{ start: 6, end: 4 }} showCopy={true} hoverShowCopy={true} />
            ))}
          </div>
        );
      }

      return <>Empty</>;
    },
  },
  {
    title: 'token',
    dataIndex: 'asset_id',
    key: 'asset_id',
    width: 100,
    align: 'left',
    render: (_, record: ITransaction) => {
      const asset = getTransactionAssetId(record);
      if (!asset) return null;
      return <AssetSymbol assetId={asset} chainId={chainId} />;
    },
  },
  {
    title: () => (
      <span>
        Amount{' '}
        <Tooltip title="Total transaction amount processed." overlayInnerStyle={{ width: '180px' }}>
          <QuestionCircleOutlined style={{ color: 'rgba(20, 20, 0, 0.6)', fontSize: '14px' }} />
        </Tooltip>
      </span>
    ),
    dataIndex: 'amount',
    key: 'amount',
    width: 120,
    align: 'left',
    render: (_, record: ITransaction) => {
      const amount = getTransactionAmount(record);
      const asset = getTransactionAssetId(record);
      if (!asset) return null;
      return <AssetAmount assetId={asset} chainId={chainId} amount={amount} displayDigits={6} />;
    },
  },
  {
    title: 'Fee',
    dataIndex: 'fee',
    key: 'fee',
    width: 140,
    align: 'left',
    render: (_: string, record: ITransaction) => {
      const protocolFees = record.protocol_fee?.map((fee) => BigInt(fee)) ?? [];
      const relayerFee = record.relayer_fee ? BigInt(record.relayer_fee) : BigInt(0);

      if (record.tx_type === TransactionType.Swap && protocolFees.length >= 2) {
        const inputAsset = record.inputs?.[0]?.asset;
        const outputAsset = record.outputs?.find((v) => v.asset !== inputAsset)?.asset;
        const inputAssetFee = protocolFees[0] + relayerFee;
        const outputAssetFee = protocolFees[1];
        if (inputAsset && outputAsset) {
          return (
            <FeeContainer>
              <FeeRow>
                <AssetAmount assetId={inputAsset} chainId={chainId} amount={inputAssetFee} displayDigits={6} /> <AssetSymbol assetId={inputAsset} chainId={chainId} />
              </FeeRow>
              <FeeRow>
                <AssetAmount assetId={outputAsset} chainId={chainId} amount={outputAssetFee} displayDigits={6} /> <AssetSymbol assetId={outputAsset} chainId={chainId} />
              </FeeRow>
            </FeeContainer>
          );
        }
      }

      const asset = getTransactionAssetId(record);
      if (!asset) return null;
      const protocolFeeSum = protocolFees.reduce((acc, curr) => acc + curr, BigInt(0));

      return (
        <FeeRow>
          <AssetAmount assetId={asset} chainId={chainId} amount={protocolFeeSum + relayerFee} displayDigits={6} /> <AssetSymbol assetId={asset} chainId={chainId} />
        </FeeRow>
      );
    },
  },
  {
    title: 'Time',
    dataIndex: 'created_at',
    key: 'created_at',
    width: 175,
    render: (_, record: ITransaction) => <TimeText>{formatTransactionTime(record.created_at)}</TimeText>,
  },
  {
    title: 'Operate',
    key: 'operate',
    width: 60,
    render: (_, record: ITransaction) => (
      <OperateContainer>
        <OperateButton onClick={() => onViewTransaction(record)}>
          <EyeOutlined />
        </OperateButton>
      </OperateContainer>
    ),
  },
];

const OperateContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding-right: 0;
`;

const OperateButton = styled.div`
  width: 36px;
  height: 36px;
  background: #f5f5f5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgba(243, 245, 242, 0.8);
  }
`;

// Styled Components
const HashContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  .copy_icon {
    opacity: 0;
  }
  &:hover {
    .copy_icon {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    .copy_icon {
      opacity: 1;
    }
  }
`;

const HashText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  color: #141413;
`;

const TypeText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  color: #141413;
`;

const TimeText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: rgba(20, 20, 0, 0.6);
`;

const FeeContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const FeeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #141413;
`;
